/**
 * Review Card Component
 *
 * Displays a single review in the moderation queue with actions.
 *
 * Props:
 *   review      — Review object from API (camelCase fields via toJSON())
 *   onApprove   — (reviewId) => void
 *   onReject    — (reviewId) => void  (opens reason modal)
 *   onFlag      — (reviewId) => void  (opens reason modal)
 *   expanded    — boolean
 *   onToggle    — () => void  (expand/collapse full text)
 *   disabled    — boolean (action in progress)
 *
 * Story: SCRUM-840
 */

import React from 'react';
import { Star, CheckCircle, XCircle, Flag, ChevronDown, ChevronUp } from 'lucide-react';
import StatusBadge from '../common/StatusBadge';

const STATUS_VARIANT_MAP = {
  pending: { label: 'Pending', variant: 'warning' },
  flagged: { label: 'Flagged', variant: 'neutral' },
  approved: { label: 'Approved', variant: 'success' },
  rejected: { label: 'Rejected', variant: 'error' },
};

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-1" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-4 h-4 ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
        />
      ))}
      <span className="ml-1 text-sm text-gray-600">{rating}/5</span>
    </div>
  );
}

export default function ReviewCard({
  review,
  onApprove,
  onReject,
  onFlag,
  expanded = false,
  onToggle,
  disabled = false,
}) {
  const badge = STATUS_VARIANT_MAP[review.moderationStatus] || STATUS_VARIANT_MAP.pending;
  const commentText = review.comment || '';
  const isLong = commentText.length > 200;
  const displayText = expanded || !isLong ? commentText : `${commentText.slice(0, 200)}...`;
  const submittedDate = review.createdAt
    ? new Date(review.createdAt).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    })
    : 'Unknown';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 space-y-3">
      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-3">
          <StatusBadge variant={badge.variant}>
            {badge.label}
          </StatusBadge>
          <StarRating rating={review.overallRating} />
        </div>
        <span className="text-sm text-gray-500">{submittedDate}</span>
      </div>

      {/* Title */}
      {review.title && (
        <h3 className="text-sm font-semibold text-gray-900">{review.title}</h3>
      )}

      {/* Comment */}
      <p className="text-sm text-gray-700 whitespace-pre-wrap">{displayText}</p>
      {isLong && (
        <button
          type="button"
          onClick={onToggle}
          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
        >
          {expanded ? (
            <>Show less <ChevronUp className="w-3 h-3" /></>
          ) : (
            <>Show more <ChevronDown className="w-3 h-3" /></>
          )}
        </button>
      )}

      {/* Meta */}
      <div className="flex flex-wrap gap-4 text-xs text-gray-500">
        <span>Reviewer: {review.reviewerId?.slice(0, 8) || 'N/A'}</span>
        <span>Experience: {review.experienceId?.slice(0, 8) || 'N/A'}</span>
        <span>Guide: {review.guideId?.slice(0, 8) || 'N/A'}</span>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
        <button
          type="button"
          onClick={() => onApprove(review.reviewId)}
          disabled={disabled}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 disabled:opacity-50"
        >
          <CheckCircle className="w-4 h-4" />
          Approve
        </button>
        <button
          type="button"
          onClick={() => onReject(review.reviewId)}
          disabled={disabled}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50"
        >
          <XCircle className="w-4 h-4" />
          Reject
        </button>
        {review.moderationStatus !== 'flagged' && (
          <button
            type="button"
            onClick={() => onFlag(review.reviewId)}
            disabled={disabled}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-orange-700 bg-orange-50 rounded-lg hover:bg-orange-100 disabled:opacity-50"
          >
            <Flag className="w-4 h-4" />
            Flag
          </button>
        )}
      </div>
    </div>
  );
}
